import { Constants, _adapterRegistry } from '../shared/Constants';
import { renderBadgeCell } from '../features/formatting/renderBadgeCell';
import { BadgeMappingBuilder } from './BadgeMappingBuilder';
import { ButtonMappingBuilder } from './ButtonMappingBuilder';
import { renderButtonCell } from '../features/formatting/renderButtonCell';

/**
 * ColumnBuilder Class
 * @template {string} K
 */
export class ColumnBuilder {
    constructor() {
        this._column = {
            data: null,
            title: '',
            orderable: true,
            searchable: true,
            className: '',
            width: undefined,
            render: null,
            filterType: null,
            visible: true,
            noToggle: false,
            _actionCallbacks: {}
        };
    }

    /**
     * Sets whether the column is visible by default.
     * @param {boolean} [isVisible=false]
     * @returns {this}
     */
    hidden(isVisible = false) {
        this._column.visible = isVisible;
        return this;
    }

    /**
     * Alias for hidden(true) or hidden(false).
     * @param {boolean} isVisible
     * @returns {this}
     */
    visible(isVisible) {
        this._column.visible = isVisible;
        return this;
    }

    /**
     * Prevents this column from appearing in the visibility management UI (colvis).
     * @param {boolean} [noToggle=true]
     * @returns {this}
     */
    noToggle(noToggle = true) {
        this._column.noToggle = noToggle;
        if (noToggle) {
            this._column.className = (this._column.className || '') + ' dt-no-toggle';
        }
        return this;
    }

    withFilter(config = 'text') {
        this._column.filterType = typeof config === 'string' ? config : 'custom';
        this._column.filterConfig = typeof config === 'object' ? config : null;
        return this;
    }

    /**
     * @template {string} NK
     * @param {NK} dataName
     * @returns {ColumnBuilder<NK>}
     */
    withData(dataName) {
        this._column.data = dataName;
        return /** @type {any} */ (this);
    }

    withTitle(title) {
        this._column.title = title;
        return this;
    }

    withWidth(width) {
        this._column.width = width;
        // A caller-set width wins over the auto-widening done by sortable().
        this._column._checkboxWidthAuto = false;
        return this;
    }

    withClass(className) {
        this._column.className += ` ${className}`;
        return this;
    }

    withAlign(align) {
        const validAligns = Object.values(Constants.Align);
        if (!validAligns.includes(align)) {
            const key = String(align).toUpperCase();
            if (Constants.Align[key]) {
                align = Constants.Align[key];
            } else {
                console.warn(`ColumnBuilder: Invalid alignment "${align}". Valid values: ${validAligns.join(', ')}`);
                return this;
            }
        }
        this._column.className += ` ${align}`;
        return this;
    }

    notSortable() {
        this._column.orderable = false;
        this._column._checkboxSortable = false;
        if (this._column._isCheckbox) {
            this._column.className = this._column.className.replace(/\s*dt-checkbox-sortable/, '');
            // Only undo the widening sortable() applied, never a caller's width.
            if (this._column._checkboxWidthAuto) {
                this._column.width = '44px';
                this._column._checkboxWidthAuto = false;
            }
            this._buildCheckboxRender();
        }
        return this;
    }

    /**
     * Makes the column sortable. On a checkbox column this sorts by checked
     * state (checked first ascending, checked last descending), using the live
     * selection set as the key. Rows the checkbox condition hides sort after
     * every checkbox row when ascending.
     * Checkbox columns are already sortable by default — use this to re-enable
     * after .notSortable().
     * @returns {ColumnBuilder<''>}
     */
    sortable() {
        this._column.orderable = true;
        if (this._column._isCheckbox) {
            this._column._checkboxSortable = true;
            // Widen to leave room for the sort glyphs, unless the caller set a width.
            if (this._column.width === undefined || this._column._checkboxWidthAuto || this._column.width === '44px') {
                this._column.width = '64px';
                this._column._checkboxWidthAuto = true;
            }
            if (!this._column.className.includes('dt-checkbox-sortable')) {
                this._column.className += ' dt-checkbox-sortable';
            }
            this._buildCheckboxRender();
        }
        return this;
    }

    /**
     * @param {function(any): boolean} [conditionFn] - Optional predicate to show checkbox only for certain rows
     * @returns {ColumnBuilder<''>}
     */
    withCheckbox(conditionFn) {
        this._column.data = null;
        this._column.searchable = false;
        this._column.className = 'select-checkbox text-center';
        this._column.title = '<input type="checkbox" class="dt-header-select-all"><div class="dt-checkbox-wrapper"></div>';
        this._column._checkboxCondition = conditionFn;
        this._column._isCheckbox = true;
        this._column.noToggle = true;

        // Sortable by default, like every other column; .notSortable() opts out.
        this.sortable();
        return /** @type {any} */ (this);
    }

    withBadge(mappingFn) {
        const builder = new BadgeMappingBuilder();
        mappingFn(builder);
        const mappings = builder._getMappings();
        const defaultMapping = builder._getDefault();

        this._column.render = (data, type, row) => {
            if (data === null || data === undefined) return '';

            const match = mappings.find(m => m.val === data);
            if (match) return renderBadgeCell(match, data, row);
            if (defaultMapping) return renderBadgeCell(defaultMapping, data, row);
            return data;
        };
        return this;
    }

    /**
     * Renders a button inside the cell based on a value->button mapping (similar to withBadge).
     *
     * Buttons use `data-action="<key>"` so they participate in the same delegated click handling
     * as `.withActions()` and toolbar buttons. Any `.onProcess(...)` callbacks registered here
     * are automatically attached to the table's action registry.
     */
    withButton(mappingFn) {
        const builder = new ButtonMappingBuilder();
        mappingFn(builder);
        const mappings = builder._getMappings();
        const defaultMapping = builder._getDefault();

        const registerCallbacks = (m) => {
            if (m && m.key && typeof m.callback === 'function') {
                this._column._actionCallbacks[m.key] = m.callback;
            }
        };

        mappings.forEach(registerCallbacks);
        registerCallbacks(defaultMapping);

        this._column.render = (data, type, row) => {
            if (data === null || data === undefined) return '';

            const match = mappings.find((m) => m.val === data);
            if (match) return renderButtonCell(match, data, row);
            if (defaultMapping) return renderButtonCell(defaultMapping, data, row);
            return data;
        };

        // Button columns are typically not used for filtering/sorting
        this._column.orderable = false;
        this._column.searchable = false;
        return this;
    }

    withDate(format = undefined) {
        // Declare the column a date so DataTables skips string-based type sniffing.
        // Without this, MM/DD/YYYY fails DataTables' date regex and the column is
        // typed "string", which sorts month-by-month instead of chronologically.
        this._column.sType = 'date';
        this._column.render = (data, type) => {
            if (!data) return '';
            if (type === 'display') {
                const gd = window.GenericDateTime ? window.GenericDateTime.from(data) : null;
                if (!gd || !gd.isValid()) return data;
                if (format) gd.withFormat(format);
                return gd.asString();
            }
            // Sort/type get a chronological key; search and filtering keep the raw value.
            if (type === 'sort' || type === 'type') {
                const gd = window.GenericDateTime ? window.GenericDateTime.from(data) : null;
                return gd && gd.isValid() ? gd.asISO() : data;
            }
            return data;
        };
        return this;
    }

    withCurrency(symbol = Constants.Currency.PHP) {
        this._column.className += ` ${Constants.Align.RIGHT}`;
        this._column.render = (data) => {
            if (data === null || data === undefined) return '';
            const val = parseFloat(data);
            return `${symbol} ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };
        return this;
    }

    withRender(renderFn) {
        this._column._userRender = renderFn;
        if (this._column._isCheckbox) {
            this._buildCheckboxRender();
        } else {
            this._column.render = renderFn;
        }
        return this;
    }

    /**
     * Supply a client-side ordering key based on the value, row and selection.
     * Return a consistently typed string or finite number for every row.
     * @param {function(any, any, {isSelected: boolean, hasCheckbox: boolean}): (string|number)} sortKeyFn
     * @returns {this}
     */
    withSortKey(sortKeyFn) {
        if (typeof sortKeyFn !== 'function') throw new TypeError('withSortKey expects a function');
        this._column._sortKey = sortKeyFn;
        return this;
    }

    _buildCheckboxRender() {
        const conditionFn = this._column._checkboxCondition;
        const userRender = this._column._userRender;
        const sortable = this._column._checkboxSortable;

        this._column.render = (data, type, row, meta) => {
            // '0' checked, '1' unchecked, '2' no checkbox.
            // Ascending: checked, unchecked, no checkbox. Descending reverses it.
            if (sortable && (type === 'sort' || type === 'type')) {
                if (typeof conditionFn === 'function' && !conditionFn(row)) return '2';
                const adapter = meta?.settings?.nTable ? _adapterRegistry.get(meta.settings.nTable) : null;
                return adapter && adapter.isRowSelectedIndex(meta.row) ? '0' : '1';
            }
            if (typeof conditionFn === 'function' && !conditionFn(row)) {
                return '';
            }
            // When user provides a custom render, only inject the hidden
            // .dt-row-select input for selection state tracking — the user's
            // render provides the visual. No .dt-checkbox-wrapper div.
            if (typeof userRender === 'function') {
                const userHtml = userRender(data, type, row, meta);
                return `<input type="checkbox" class="dt-row-select">${userHtml}`;
            }
            return '<input type="checkbox" class="dt-row-select"><div class="dt-checkbox-wrapper"></div>';
        };
    }

    build() {
        if (this._column._sortKey) {
            const column = this._column;
            const render = column.render;
            // A formatter such as withDate may have forced a type for its own key.
            // Let DataTables detect the type of the custom key instead.
            delete column.sType;
            column.render = (data, type, row, meta) => {
                if (type === 'sort' || type === 'type') {
                    const adapter = meta?.settings?.nTable ? _adapterRegistry.get(meta.settings.nTable) : null;
                    const hasCheckbox = !!column._isCheckbox &&
                        (typeof column._checkboxCondition !== 'function' || !!column._checkboxCondition(row));
                    const key = column._sortKey(data, row, {
                        isSelected: !!(adapter && adapter.isRowSelectedIndex(meta.row)),
                        hasCheckbox
                    });
                    if (typeof key !== 'string' && !(typeof key === 'number' && Number.isFinite(key))) {
                        throw new TypeError('withSortKey must return a string or finite number');
                    }
                    return key;
                }
                return typeof render === 'function' ? render(data, type, row, meta) : data;
            };
        }
        return this._column;
    }
}
